import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";
import { useLocation } from "react-router-dom";

/**
 * Whether the header search should search chats/messages instead of explore.
 * @param {string} pathname
 * @returns {boolean}
 */
export function isInboxSearchPath(pathname) {
  if (!pathname) return false;
  if (pathname === "/messaging" || pathname.startsWith("/messaging/")) {
    return true;
  }
  return /^\/organization\/[^/]+\/channels(?:\/|$)/.test(pathname);
}

const MessagingSearchContext = createContext({
  query: "",
  setQuery: () => {},
  showResults: false,
  dismissResults: () => {},
  isInboxSearch: false,
});

/**
 * Header search state for /messaging and org channel pages.
 * @param {{ children: React.ReactNode }} props
 * @returns {JSX.Element}
 */
export function MessagingSearchProvider({ children }) {
  const location = useLocation();
  const isInboxSearch = isInboxSearchPath(location.pathname);
  const [query, setQueryState] = useState("");
  const [dismissed, setDismissed] = useState(false);

  const setQuery = useCallback((value) => {
    setQueryState(value);
    setDismissed(false);
  }, []);

  const dismissResults = useCallback(() => {
    setDismissed(true);
  }, []);

  const showResults = isInboxSearch && query.trim() !== "" && !dismissed;

  const value = useMemo(
    () => ({
      query,
      setQuery,
      showResults,
      dismissResults,
      isInboxSearch,
    }),
    [query, setQuery, showResults, dismissResults, isInboxSearch]
  );

  return (
    <MessagingSearchContext.Provider value={value}>
      {children}
    </MessagingSearchContext.Provider>
  );
}

/**
 * @returns {{
 *   query: string,
 *   setQuery: (value: string) => void,
 *   showResults: boolean,
 *   dismissResults: () => void,
 *   isInboxSearch: boolean,
 * }}
 */
export function useMessagingSearch() {
  return useContext(MessagingSearchContext);
}
